<template>
    <div class="clients_card">
        <div class="card_header is-flex is-align-items-center">
            <PieChartIcon class="header_icon" />
            <span class="card_title">Clientes</span>
        </div>

        <div class="search_wrap">
            <svg class="search_icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2"
                stroke-linecap="round" stroke-linejoin="round"
                aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M21 21l-4.35-4.35" />
            </svg>
            <input type="search"
                class="search_input"
                :value="value"
                placeholder="Buscar clientes"
                @input="$emit('input', $event.target.value)" />
        </div>

        <ul v-if="clients.length > 0"
            class="ranking_list"
            :class="{ 'has-focus': hasHighlight }">
            <li v-for="client in clients" :key="client.name"
                class="ranking_item"
                :class="{ 'is-dimmed': hasHighlight && !isHighlighted(client) }"
                role="button"
                tabindex="0"
                @click="onClientClick(client)"
                @keydown.enter.prevent="onClientClick(client)"
                @keydown.space.prevent="onClientClick(client)">
                <span class="ranking_label"
                    v-tooltip.top="client.name.length > 20 ? client.name : ''">
                    {{ client.name }}
                </span>
                <div class="ranking_bar_track">
                    <div class="ranking_bar_fill"
                        :style="{ width: barWidth(client.share) }"></div>
                </div>
                <span class="ranking_meta">
                    <span class="ranking_amount">{{ formatMoney(client.amount) }}</span>
                    <span class="ranking_dot" aria-hidden="true">·</span>
                    <span class="ranking_share">{{ formatShare(client.share) }}</span>
                </span>
            </li>
        </ul>

        <p v-else class="empty_state">
            Sin resultados para "{{ value }}".
        </p>
    </div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import PieChartIcon from "@/components/svg/PieChartIcon.vue";

export default {
    name: "ClientsRankingCard",
    components: { PieChartIcon },
    // v-model: value/input. El padre (BodySalesAnalysis) hace el bridge con
    // el store. La card queda presentational — los specs no necesitan Vuex.
    model: { prop: "value", event: "input" },
    props: {
        clients: {
            type: Array,
            required: true,
            validator: (arr) => arr.every((c) => typeof c.name === "string"),
        },
        value: { type: String, default: "" },
        // Set de nombres de clientes a resaltar — los que NO están en el set
        // se atenúan con opacity + color muted. `null` significa "sin foco",
        // todos se ven normal. Lo pasa el padre cuando el usuario hovea un
        // valor en `StoresDistributionCard` (ej. hover sobre "Tienda Centro"
        // → set = clientes asociados a esa tienda).
        highlightedClients: { type: Set, default: null },
    },
    computed: {
        hasHighlight() {
            return this.highlightedClients instanceof Set
                && this.highlightedClients.size > 0;
        },
    },
    methods: {
        sharePercent(share) {
            const v = Math.max(0, Math.min(1, share ?? 0));
            return Math.round(v * 100);
        },
        barWidth(share) {
            return `${this.sharePercent(share)}%`;
        },
        formatMoney(v) {
            return parseToMoney(v);
        },
        formatShare(share) {
            return `${this.sharePercent(share)}%`;
        },
        isHighlighted(client) {
            return this.highlightedClients instanceof Set
                && this.highlightedClients.has(client.name);
        },
        // Drill-down contract: el caller decide qué hacer con el click. Por
        // ahora no hay listener (otra iteración abrirá un modal por cliente);
        // el evento existe para que la API del componente esté lista.
        onClientClick(client) {
            this.$emit("client-click", client);
        },
    },
};
</script>

<style scoped lang="scss">
.clients_card {
    background: var(--report-bg);
    border-radius: 16px;
    border: 1px solid var(--report-border);
    box-shadow: var(--report-shadow-card);
    padding: 20px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.card_header {
    margin-bottom: 12px;
    color: var(--report-text);
    flex-shrink: 0;

    .header_icon {
        margin-right: 8px;
        flex-shrink: 0;
    }

    .card_title {
        font-size: 14px;
        font-weight: 600;
    }
}

.search_wrap {
    position: relative;
    margin-bottom: 12px;
    flex-shrink: 0;

    .search_icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        width: 16px;
        height: 16px;
        color: var(--report-text-muted-soft);
        pointer-events: none;
    }
}

.search_input {
    width: 100%;
    // padding-left genera el espacio para el ícono absoluto a la izquierda.
    padding: 10px 12px 10px 38px;
    border: 1px solid var(--report-border);
    border-radius: 999px;
    background: var(--report-bg);
    font-size: 14px;
    color: var(--report-text);
    box-sizing: border-box;

    &::placeholder {
        color: var(--report-text-muted-soft);
    }

    &:focus {
        outline: none;
        border-color: var(--report-accent-text);
        box-shadow: 0 0 0 3px var(--report-accent-bg);
    }
}

.ranking_list {
    list-style: none;
    margin: 0;
    padding: 0;
    // El clamp lo dicta la card (overflow:hidden + flex column). Antes
    // teníamos max-height: 460px arbitrario que pesaba en cards más cortas
    // y no escalaba a viewports altos.
    flex: 1;
    min-height: 0;
    overflow-y: auto;
}

.ranking_item {
    display: grid;
    // 3 columnas: label / bar / meta. Label y meta con ancho FIJO para que la
    // barra (1fr) tenga la MISMA longitud en todas las filas. Con `auto` el
    // ancho del monto variaba según su cantidad de caracteres y comprimía la
    // barra distinto fila a fila. El fill adentro sigue reflejando el %.
    grid-template-columns: 140px 1fr 140px;
    gap: 16px;
    align-items: center;
    padding: 6px 8px;
    font-size: 13px;
    border-radius: 8px;

    // Drill-down affordance: cursor + hover + focus visible (accesibilidad
    // por teclado vía tabindex+@keydown). La transición incluye opacity
    // para el dim-out cuando el padre activa el highlight cruzado.
    cursor: pointer;
    transition: background-color 120ms ease, opacity 140ms ease;

    &:hover {
        background: var(--report-surface);
    }

    &:focus-visible {
        outline: 2px solid var(--report-accent-border);
        outline-offset: 2px;
        background: var(--report-surface);
    }

    // Cuando el padre activa highlight cruzado (hover sobre un attr de
    // StoresDistributionCard), los items NO asociados a ese attr bajan
    // a opacity 0.35 + texto muted. Los que sí están conservan el color
    // normal — el contraste de "dimmed vs normal" hace de highlight sin
    // necesidad de un border/badge extra.
    &.is-dimmed {
        opacity: 0.35;
        // Bajar también el contraste interno para que se sienta
        // "desenfocado" y no solo "transparente sobre fondo blanco".
        color: var(--report-text-muted);

        .ranking_amount,
        .ranking_share {
            color: var(--report-text-muted-soft);
        }
    }
}

.ranking_label {
    color: var(--report-text);
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.ranking_bar_track {
    background: var(--report-progress-bg);
    border-radius: 999px;
    height: 13px;
    overflow: hidden;
}

.ranking_bar_fill {
    height: 100%;
    border-radius: 999px;
    // Single accent color — sin tiers (los rankings largos no admiten
    // codificación cromática por posición). El token vive separado de los
    // chart-segment-* porque su semántica es distinta (uniform vs gradient).
    background: var(--report-chart-accent);
    transition: width 200ms ease-out;
}

.ranking_meta {
    // Columna de ancho fijo → alineamos el monto al borde derecho en todas las
    // filas (flush-right), coherente fila a fila.
    justify-self: end;
    display: inline-flex;
    align-items: baseline;
    gap: 8px;
    color: var(--report-text-muted);
    font-size: 13px;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;

    .ranking_amount {
        color: var(--report-text-muted-strong);
        font-weight: 500;
    }

    .ranking_dot {
        color: var(--report-text-muted-soft);
        font-weight: 700;
    }

    .ranking_share {
        color: var(--report-text-muted);
        font-weight: 500;
        // Ancho fijo + right-align: el "%" varía de dígitos ("2%" vs "100%") y
        // sin esto corría el monto de su izquierda. Fijándolo, lo que hay a la
        // derecha del monto es constante → los montos quedan alineados en
        // columna (flush-right) fila a fila.
        min-width: 36px;
        text-align: right;
    }
}

.empty_state {
    color: var(--report-text-muted);
    font-size: 13px;
    text-align: center;
    padding: 24px 8px;
}
</style>
